import { useRef, useState } from 'react';
import { ChevronsLeftRight } from 'lucide-react';

export default function BeforeAfter({ beforeImage, afterImage, alt, labels, aspectClass = 'aspect-[16/10]' }) {
  const [pos, setPos] = useState(6);
  const [interacted, setInteracted] = useState(false);
  const ref = useRef(null);

  const update = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.min(96, Math.max(4, p)));
  };

  const onPointerDown = (e) => {
    setInteracted(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    update(e.clientX);
  };

  const onPointerMove = (e) => {
    if (e.buttons === 1) update(e.clientX);
  };

  const onKeyDown = (e) => {
    setInteracted(true);
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setPos((p) => Math.max(4, p - 5));
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setPos((p) => Math.min(96, p + 5));
    }
  };

  return (
    <div
      ref={ref}
      role="slider"
      tabIndex={0}
      aria-label={alt}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onKeyDown={onKeyDown}
      className={`relative ${aspectClass} w-full cursor-ew-resize touch-none select-none overflow-hidden`}
      data-testid="before-after"
    >
      <img
        src={beforeImage}
        alt={alt}
        loading="lazy"
        draggable={false}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={afterImage}
          alt=""
          aria-hidden="true"
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
      <span className="pointer-events-none absolute left-4 top-4 bg-forest/90 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-white">
        {labels.after}
      </span>
      <span className="pointer-events-none absolute right-4 top-4 bg-ink/70 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-white">
        {labels.before}
      </span>
      <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -left-px w-0.5 bg-white/90" aria-hidden="true" />
        <div
          className={`absolute top-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-lg${interacted ? '' : ' animate-handle-pulse'}`}
          style={{ transform: 'translate(-50%, -50%)' }}
          aria-hidden="true"
        >
          <ChevronsLeftRight className="h-5 w-5 text-ink" />
        </div>
      </div>
      <span className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 bg-ink/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80">
        {labels.hint}
      </span>
    </div>
  );
}
